import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, CheckCircle, Download, Home, RotateCcw, Share2, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useProgress } from '../context/ProgressContext';
import { usePurchase } from '../context/PurchaseContext';
import { courseModules } from '../data/courseData';
import { toast } from 'sonner';

export default function CertificatePage() {
  const navigate = useNavigate();
  const { progress, getOverallProgress, resetProgress } = useProgress();
  const { testerMode, hasPurchased } = usePurchase();
  const certificateRef = useRef(null);
  
  // Check if in tester mode (not purchased, but has tester access)
  const isTesterMode = testerMode.isActive && !hasPurchased;
  
  const overallProgress = getOverallProgress();
  const isComplete = overallProgress === 100;
  
  const completionDate = progress.completedAt 
    ? new Date(progress.completedAt).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : new Date().toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Digital Safety Certificate',
          text: "I've completed the Digital Safety Course and earned my certificate!",
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(
        `I've completed the Digital Safety Course! ${window.location.origin}`
      );
      toast.success('Link copied to clipboard!');
    }
  };

  const handleRestartCourse = () => {
    if (window.confirm('Are you sure you want to restart the course? Your progress will be reset, but you can earn your certificate again.')) {
      resetProgress();
      navigate('/module/introduction');
      toast.success('Course restarted!', {
        description: 'Your progress has been reset. Good luck!',
      });
    }
  };

  if (!isComplete) {
    return (
      <div className="max-w-2xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-8 md:p-12">
          <div className="w-20 h-20 bg-stone-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Award className="w-10 h-10 text-stone-400" />
          </div>
          
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
            Certificate Locked
          </h1>
          
          <p className="text-text-muted text-lg mb-6">
            Complete all 12 modules to unlock your certificate!
          </p>
          
          <div className="bg-stone-50 rounded-xl p-4 mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-text-muted">Your progress</span>
              <span className="font-bold text-primary">{overallProgress}%</span>
            </div>
            <div className="h-4 bg-stone-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            <p className="text-sm text-text-muted mt-2">
              {progress.completedModules.length} of {courseModules.length} modules completed
            </p>
          </div>
          
          <Link to={`/module/${courseModules[progress.completedModules.length]?.slug || 'introduction'}`}>
            <Button className="bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold">
              Continue Learning
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto fade-in">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 no-print">
        <Link to="/">
          <Button variant="outline" className="rounded-full px-6 py-4 font-semibold">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </Link>
        
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={handleShare}
            className="rounded-full px-6 py-4 font-semibold"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <Button 
            onClick={handlePrint}
            className="bg-primary text-white hover:bg-primary-600 rounded-full px-6 py-4 font-semibold"
          >
            <Download className="w-4 h-4 mr-2" />
            Print Certificate
          </Button>
        </div>
      </div>

      {/* Certificate */}
      <div 
        ref={certificateRef}
        className="certificate bg-gradient-to-br from-secondary-50 via-white to-secondary-50 rounded-3xl p-8 md:p-12 shadow-xl relative overflow-hidden"
        data-testid="certificate"
      >
        {/* TEST Watermark for tester mode */}
        {isTesterMode && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div 
              className="text-[120px] md:text-[180px] font-extrabold text-red-500/20 transform -rotate-45 select-none"
              style={{ letterSpacing: '0.2em' }}
            >
              TEST
            </div>
          </div>
        )}
        
        {/* Border decoration */}
        <div className="border-4 border-double border-secondary/50 rounded-2xl p-6 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-secondary rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
              <Award className="w-10 h-10 text-secondary-900" />
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-primary mb-2">
              Certificate of Completion
            </h1>
            <p className="text-text-muted">Digital Safety Course</p>
          </div>

          {/* Main content */}
          <div className="text-center mb-8">
            <p className="text-text-muted text-lg mb-2">This certifies that</p>
            <div className="border-b-2 border-primary/30 pb-2 mb-6 mx-auto max-w-md">
              <p className="font-heading font-bold text-2xl text-primary">
                A Committed Learner
              </p>
            </div>
            <p className="text-text-muted text-lg mb-6">
              has successfully completed all modules of the
            </p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
              Australian Digital Safety Workbook Course
            </h2>
            <p className="text-text-muted max-w-xl mx-auto">
              demonstrating understanding of essential online safety practices including 
              password security, device protection, scam awareness, and privacy management.
            </p>
          </div>

          {/* Modules completed */}
          <div className="bg-white/50 rounded-xl p-6 mb-8 max-w-2xl mx-auto">
            <h3 className="font-heading font-bold text-lg text-text mb-4 text-center">
              Modules Completed
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {courseModules.map((module) => (
                <div 
                  key={module.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                  <span className="text-text-muted">{module.shortTitle}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-text-muted mb-2">Completed on</p>
            <p className="font-heading font-bold text-xl text-primary mb-6">
              {completionDate}
            </p>
            
            <div className="flex items-center justify-center gap-3 text-text-muted text-sm">
              <img 
                src="https://customer-assets.emergentagent.com/job_aussiesafety/artifacts/17uanq9m_BTA_Logo_NoBgnd.png"
                alt="Bellarine Tech Assist Logo"
                className="w-8 h-8 object-contain"
              />
              <span>Based on the Digital Safety Workbook by</span>
              <span className="font-semibold">Bellarine Tech Assist</span>
            </div>
            
            {/* Tester mode notice */}
            {isTesterMode && (
              <div className="mt-4 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg inline-block">
                ⚠️ Preview certificate - Purchase the course for an official certificate
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Post-completion actions */}
      <div className="mt-8 no-print space-y-6">
        {/* Celebration message */}
        <div className="bg-success/10 border border-success/30 rounded-2xl p-6 text-center">
          <h2 className="font-heading font-bold text-xl text-success mb-2">
            🎉 Well Done!
          </h2>
          <p className="text-text-muted mb-4">
            You've completed the Digital Safety Course! Remember, online safety is an ongoing practice. 
            Keep using what you've learned and stay vigilant.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/">
              <Button variant="outline" className="rounded-full px-6 py-4 font-semibold">
                <Home className="w-4 h-4 mr-2" />
                Revisit Modules
              </Button>
            </Link>
            <Button 
              variant="outline"
              onClick={handleRestartCourse}
              data-testid="restart-course"
              className="rounded-full px-6 py-4 font-semibold"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart Course
            </Button>
          </div>
        </div>

        {/* Book prompts */}
        <div className="bg-primary/5 rounded-2xl p-6">
          <div className="text-center mb-4">
            <BookOpen className="w-8 h-8 text-primary mx-auto mb-2" />
            <h3 className="font-heading font-bold text-lg text-text">
              Continue Your Learning Journey
            </h3>
            <p className="text-text-muted text-sm">
              The full Digital Safety Workbook has even more tips, exercises, and resources.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-digital-edition?variant=56047764865190"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-stone-50 rounded-xl text-primary font-medium transition-colors border border-primary/20"
            >
              <BookOpen className="w-4 h-4" />
              Get Digital Workbook
              <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="https://bellarine-tech-assist-2.myshopify.com/products/%F0%9F%93%98-digital-safety-workbook-printed-edition?variant=56047888105638"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-stone-50 rounded-xl text-primary font-medium transition-colors border border-primary/20"
            >
              <BookOpen className="w-4 h-4" />
              Order Printed Copy
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
